import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  clearDuctedPairPick,
  setDuctedPagination,
  setDuctedPairPick
} from 'features/ui';
import {
  isPickedForPair,
  selectDuctedBundleMode,
  selectDuctedBundleSlots
} from 'utils/ducted-bundle-picks';
import type { DuctedBundleSlot } from 'utils/ducted-bundle-picks-types';
import { compareStateForCard } from 'utils/ducted-compare-state';
import {
  isDuctedRailSectionSkipped,
  listRankGroupSelections
} from 'utils/ducted-rail-selections';
import { unevaluatedFilterLabels } from 'utils/ducted-ratings';
import { canSelectCombination } from 'utils/quotable-add-pipeline';
import CombinationComponentImageStrip from './combination-component-image-strip';
import { pairSelectLabels } from './compare-action-text';
import DuctedBundlePickFooter from './ducted-bundle-pick-footer';
import DuctedCombinationCard from './ducted-combination-card';
import DuctedRailSectionHeading from './ducted-rail-section-heading';
import {
  hasDuctedTonnageFilter,
  isNoCoolingSideCombination
} from './ducted-rail-sections';
import { DuctedPanelSelection } from './ducted-system-detail-panel';
import SwapCardWrapper from './swap-card-wrapper';
import useDuctedRailSections from './use-ducted-rail-sections';

type DuctedSystemListPanelProps = Readonly<{
  comparedIdByCardId: ReadonlyMap<string, string>;
  selectedId: string | null;
  disabledIds: ReadonlySet<string>;
  onSelect: (selection: DuctedPanelSelection) => void;
  onAddBundle: (slots: DuctedBundleSlot[]) => void;
}>;

export default function DuctedSystemListPanel({
  comparedIdByCardId,
  selectedId,
  disabledIds,
  onSelect,
  onAddBundle
}: DuctedSystemListPanelProps) {
  const dispatch = useAppDispatch();
  const { sections } = useDuctedRailSections();
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const ductedFilters = useAppSelector((state) => state.ui.ductedFilters);
  const tonnageFilterActive = hasDuctedTonnageFilter(ductedFilters.filters);
  const bundleMode = useAppSelector(selectDuctedBundleMode);
  const bundleSlots = useAppSelector(selectDuctedBundleSlots);
  const ductedPairPicks = useAppSelector((state) => state.ui.ductedPairPicks);
  const bundleSlotFor = (systemUniqueId: string): DuctedBundleSlot | null =>
    bundleMode
      ? bundleSlots.find((slot) => slot.systemUniqueId === systemUniqueId) ??
        null
      : null;

  const visibleSections = sections.filter(
    (section) => !isDuctedRailSectionSkipped(section)
  );
  const groups = visibleSections.map((section) => ({
    section,
    selections: section.rankGroups.flatMap((group) =>
      listRankGroupSelections(section, group)
    )
  }));
  const subSectionEntries = visibleSections.flatMap(
    (section) => section.subSections
  );
  const shownCount = groups.reduce(
    (sum, { selections }) => sum + selections.length,
    0
  );
  const totalCount = subSectionEntries.reduce(
    (sum, subSection) => sum + subSection.displayTotalCount,
    0
  );
  const isLoading = subSectionEntries.some(
    (subSection) => subSection.entry?.status === 'loading'
  );
  const serverHasMore = subSectionEntries.some(
    (subSection) =>
      (subSection.entry?.combinations.length ?? 0) <
      (subSection.entry?.totalCount ?? 0)
  );

  const handleLoadMore = () => {
    dispatch(setDuctedPagination({ pageNumber: ductedFilters.pageNumber + 1 }));
  };

  const togglePairPick = (
    slot: DuctedBundleSlot,
    selection: DuctedPanelSelection
  ) => {
    if (
      isPickedForPair(ductedPairPicks, slot.systemUniqueId, selection.cardId)
    ) {
      dispatch(clearDuctedPairPick(slot.systemUniqueId));
      return;
    }
    dispatch(
      setDuctedPairPick({
        systemUniqueId: slot.systemUniqueId,
        pick: { selection }
      })
    );
  };

  const cardPropsFor = (selection: DuctedPanelSelection) => ({
    combination: selection.combination,
    productData: selection.productData,
    accessoryData: selection.accessoryData,
    modelSpecs: selection.modelSpecs,
    title: selection.title,
    typeLabel: selection.typeLabel,
    keptPairing: selection.keptPairing,
    noCoolingSide: isNoCoolingSideCombination(
      selection.combination,
      tonnageFilterActive
    ),
    unevaluatedFilterLabels: unevaluatedFilterLabels(selection.combination),
    details: (
      <CombinationComponentImageStrip
        combination={selection.combination}
        productData={selection.productData}
        className='mt-3'
      />
    )
  });

  const renderSwapCard = (selection: DuctedPanelSelection) => {
    const { comparedId } = compareStateForCard(
      comparedIdByCardId,
      systemsToCompare,
      selection
    );
    const inOtherColumn =
      comparedId !== undefined && disabledIds.has(comparedId);
    const selectable = canSelectCombination(
      selection.combination,
      selection.productData
    );
    return (
      <SwapCardWrapper
        key={selection.cardId}
        disabled={inOtherColumn || !selectable}
        badge={inOtherColumn ? 'Selected' : 'Unavailable'}
      >
        <DuctedCombinationCard
          {...cardPropsFor(selection)}
          isSelected={comparedId !== undefined && comparedId === selectedId}
          onSelect={() => onSelect(selection)}
        />
      </SwapCardWrapper>
    );
  };

  const renderPairPickCard = (
    slot: DuctedBundleSlot,
    selection: DuctedPanelSelection
  ) => {
    const picked = isPickedForPair(
      ductedPairPicks,
      slot.systemUniqueId,
      selection.cardId
    );
    const selectable = canSelectCombination(
      selection.combination,
      selection.productData
    );
    return (
      <SwapCardWrapper
        key={selection.cardId}
        disabled={!selectable && !picked}
        badge='Unavailable'
      >
        <DuctedCombinationCard
          {...cardPropsFor(selection)}
          isSelected={picked}
          onSelect={() => togglePairPick(slot, selection)}
          isAddedToCompare={picked}
          onToggleCompare={() => togglePairPick(slot, selection)}
          compareLabels={pairSelectLabels(slot.pairName)}
        />
      </SwapCardWrapper>
    );
  };

  return (
    <div
      data-testid='ducted-system-list-panel'
      className='flex max-h-[70vh] w-[360px] flex-col'
    >
      <div className='shrink-0 border-b border-gray-200 bg-white px-3 pb-2 pt-3'>
        <h2 className='text-lg font-bold text-gray-900'>System Options</h2>
        <p className='mt-1 text-xs text-gray-500'>
          {bundleMode
            ? 'Pick one system for each outdoor unit, then add the set to this column.'
            : 'Select a system to swap into this column.'}
        </p>
        {totalCount > 0 && (
          <p className='mt-0.5 text-xs text-gray-500'>
            Showing {Math.min(shownCount, totalCount)} of {totalCount} system
            {totalCount === 1 ? '' : 's'}
          </p>
        )}
      </div>
      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3'>
        {shownCount === 0 && (
          <div className='py-2 text-center text-xs text-gray-500'>
            {isLoading ? 'Loading systems...' : 'No systems available.'}
          </div>
        )}
        {groups.map(({ section, selections }) => {
          const bundleSlot = bundleSlotFor(section.system.uniqueId);
          return (
            <div key={section.system.uniqueId} className='flex flex-col gap-2'>
              <DuctedRailSectionHeading
                systemUniqueId={section.system.uniqueId}
                bundleSlot={bundleSlot}
              />
              {section.subSections.some(
                (subSection) => subSection.entry?.status === 'error'
              ) && (
                <div className='text-xs text-red-600'>
                  Couldn&apos;t load compatible systems
                </div>
              )}
              {selections.map((selection) =>
                bundleSlot
                  ? renderPairPickCard(bundleSlot, selection)
                  : renderSwapCard(selection)
              )}
            </div>
          );
        })}
        {serverHasMore && (
          <button
            type='button'
            disabled={isLoading}
            className='inline-flex items-center justify-center gap-1 self-center px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-400'
            onClick={handleLoadMore}
          >
            {isLoading ? 'Loading more...' : 'Show more'}
          </button>
        )}
      </div>
      {bundleMode && bundleSlots.length > 0 && (
        <DuctedBundlePickFooter
          slots={bundleSlots}
          onClearPick={(systemUniqueId) =>
            dispatch(clearDuctedPairPick(systemUniqueId))
          }
          onAdd={onAddBundle}
        />
      )}
    </div>
  );
}
